/**
 * Public Privacy Policy + Messaging Terms (A2P/10DLC).
 * Copy mirrors backend/sessions/communication_consent.py (version 2026-06-16-a2p-v1) —
 * the legal-approved source. Routes are public (no login). Made legal-final and
 * de-drafted 2026-07-27 (A2P resume WO): placeholders filled with approved values,
 * HIPAA/FERPA supplement clause added verbatim.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Link } from '@mui/material';

const HELP_PHONE = '718-686-5900';
const HELP_EMAIL = 'communications@hasc.net';
const LEGAL_ENTITY = 'Hebrew Academy for Special Children, Inc.';
const BUSINESS_ADDRESS = '6701 Bay Parkway, 2nd Floor, Brooklyn, NY 11204';
const LAST_UPDATED = 'July 27, 2026';

/** Verbatim MESSAGING_PRIVACY_SUPPLEMENT (backend communication_consent.py). */
const HIPAA_FERPA_SUPPLEMENT =
  "This Messaging Privacy section supplements, and does not replace, HASC's HIPAA Notice "
  + "of Privacy Practices, FERPA notices, and other applicable privacy policies. Nothing in "
  + "these Messaging Terms changes HASC's obligations under HIPAA, FERPA, or applicable "
  + 'student privacy laws.';

function PolicyShell({ title, children }) {
  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 3 }, py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {LEGAL_ENTITY} · Last updated: {LAST_UPDATED}
      </Typography>
      <Box sx={{ fontSize: '1rem', lineHeight: 1.7, color: 'text.primary', '& p': { mb: 2 } }}>
        {children}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        See also:{' '}
        <Link component={RouterLink} to="/privacy">Privacy Policy</Link>
        {' · '}
        <Link component={RouterLink} to="/messaging-terms">Messaging Terms</Link>
      </Typography>
    </Box>
  );
}

export function PrivacyPolicyPage() {
  return (
    <PolicyShell title="Privacy Policy — Text Messaging & Automated Calls">
      <Typography component="p">
        <strong>{LEGAL_ENTITY}</strong> (HASC) collects the mobile phone number(s) you
        provide in order to send you text messages and place automated or artificial-intelligence (AI) voice calls
        related to your child&apos;s program and school operations.
      </Typography>
      <Typography component="p">
        <strong>
          We do not sell, rent, or share your phone number or opt-in information with third parties or affiliates
          for their own marketing purposes.
        </strong>{' '}
        Phone numbers and consent data are used only to deliver the communications you have agreed to, and are shared
        only with the service providers that operate our messaging and calling platform on our behalf (for example, our
        communications provider), under contract and solely to provide that service.
      </Typography>
      <Typography component="p">
        You may opt out of text messages at any time by replying STOP, and revoke consent to automated or AI voice calls
        by contacting the office at {HELP_PHONE} or {HELP_EMAIL}. Standard message and data rates may apply. For
        questions, reply HELP or contact {HELP_PHONE}.
      </Typography>
      <Typography component="p">{HIPAA_FERPA_SUPPLEMENT}</Typography>
    </PolicyShell>
  );
}

export function MessagingTermsPage() {
  return (
    <PolicyShell title="SMS / Messaging Terms">
      <Typography component="p">
        <strong>Program:</strong> HASC sends school-related text messages and automated or AI voice calls (program
        updates, schedule changes, closures, emergencies, transportation, attendance, health, reminders) to
        parents and guardians who opt in.
      </Typography>
      <Typography component="p">
        <strong>Frequency:</strong> Message frequency varies.
      </Typography>
      <Typography component="p">
        <strong>Cost:</strong> Message and data rates may apply.
      </Typography>
      <Typography component="p">
        <strong>Opt-out:</strong> Reply STOP to stop texts. Reply HELP for help. To stop automated or AI voice calls,
        contact {HELP_PHONE} or {HELP_EMAIL}. Consent is optional and is not a condition of enrollment or services;
        you may revoke consent at any time.
      </Typography>
      <Typography component="p">
        <strong>Carriers:</strong> Carriers are not liable for delayed or undelivered messages.
      </Typography>
      <Typography component="p">
        <strong>Privacy:</strong> See our{' '}
        <Link component={RouterLink} to="/privacy">Privacy Policy</Link>. {HIPAA_FERPA_SUPPLEMENT}
      </Typography>
      <Typography component="p">
        <strong>Contact:</strong> {LEGAL_ENTITY} — {BUSINESS_ADDRESS} · {HELP_PHONE} · {HELP_EMAIL}
      </Typography>
    </PolicyShell>
  );
}
